/**
 * Create Order Form Component
 * Multi-step order creation wizard with validation.
 * 
 * Features:
 * - Auto-populated ZIP code lookup
 * - Client code and product selection
 * - Priority management with justification
 * - Running Caps text transformation
 * - Comprehensive form validation
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Save, Sparkles, CalendarDays, Hash, LayoutGrid, Users, MapPin, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { normalizeBorrowerName } from '../../utils/nameNormalizer';
import { normalizeStreetAddress } from '../../utils/addressNormalizer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClientProductSelector } from '../orders/ClientProductSelector';
import { ContractCodeField } from './ContractCodeField';
import { getNotesMaxLength, formatCount } from '../../utils/orderNotesLimit';
import { formatInTimeZone } from 'date-fns-tz';
import { normalizeProductId } from '../../lib/productId';
import { PriorSearchPackagePicker } from './PriorSearchPackagePicker';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const ACCOUNT_TZ = 'America/New_York';

// TITLEFLOW-CREATE-ORDER-UI-002 — premium blue/purple modal styling (presentation only)
const INPUT = 'h-10 rounded-lg border-slate-200 bg-white text-sm shadow-[0_1px_2px_rgba(15,23,42,0.03)] placeholder:text-slate-400';
const INPUT_ERR = 'border-red-400 bg-red-50';

const SectionCard = ({ index, title, icon: Icon, required, action, children }) => (
  <section
    className="co-section rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.05)]"
    style={{ '--co-i': index }}
    data-testid={`order-section-${index}`}
  >
    <header className="mb-3 flex items-center gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-xs font-bold text-white shadow-[0_2px_6px_-2px_rgba(79,70,229,.6)]">
        {index}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold leading-5 text-slate-900">
          {title}{required && <span className="text-red-500">*</span>}
        </h3>
      </div>
      {action || (Icon && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400 ring-1 ring-slate-100">
          <Icon className="h-3.5 w-3.5" />
        </span>
      ))}
    </header>
    {children}
  </section>
);

const FieldLabel = ({ children, required, optional, ...rest }) => (
  <Label className="flex items-center gap-1 text-[13px] font-semibold text-slate-700" {...rest}>
    {children}
    {required && <span className="text-red-500">*</span>}
    {optional && <span className="text-xs font-normal text-slate-400">(optional)</span>}
  </Label>
);

const FieldError = ({ msg, className = '' }) => msg ? (
  <p className={`flex items-center gap-1 text-xs text-red-600 ${className}`}>
    <span className="h-1 w-1 rounded-full bg-red-500"></span>{msg}
  </p>
) : null;

const SavePreview = ({ value, testId }) => (
  <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-500" data-testid={testId}>
    <span className="shrink-0 font-medium text-indigo-500">Will be saved as:</span>
    <span className="font-medium text-slate-700">{value}</span>
  </p>
);

// US State abbreviations for address validation
const US_STATE_ABBREVIATIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP'
];

/**
 * Validates that the property address contains only street address (Line 1)
 * and does not include City, State, or ZIP code
 * 
 * @param {string} address - The property address to validate
 * @param {string} city - The city field value (to check for duplicates)
 * @returns {object} - { isValid: boolean, error: string|null }
 */
const validateStreetAddressOnly = (address, city = '') => {
  if (!address) return { isValid: true, error: null };
  
  const trimmedAddress = address.trim();
  
  // Check for comma followed by text (typically city, state pattern)
  if (trimmedAddress.includes(',')) {
    return { 
      isValid: false, 
      error: 'Street address should not include commas. Enter only the street address (e.g., "123 Main St").' 
    };
  }
  
  // Check for ZIP code pattern - but NOT at the start (street numbers can be 5 digits)
  // Only flag ZIP codes that appear:
  // 1. At the END of the address (after space)
  // 2. After state abbreviation pattern
  // Valid: "21005 Scottsbury Dr" (21005 is street number)
  // Invalid: "Scottsbury Dr 20876" (20876 is ZIP at end)
  const words = trimmedAddress.split(/\s+/);
  if (words.length > 1) {
    // Check if the LAST word is a 5-digit number (likely ZIP)
    const lastWord = words[words.length - 1];
    if (/^\d{5}(-\d{4})?$/.test(lastWord)) {
      return { 
        isValid: false, 
        error: 'Street address should not include ZIP code. Use the ZIP Code field instead.' 
      };
    }
    
    // Check if second-to-last word is a 5-digit number followed by nothing (ZIP at end)
    // This catches "123 Main St 20876" where ZIP is at end
    if (words.length > 2) {
      const secondLastWord = words[words.length - 2];
      // If second-to-last is a ZIP and last word is a state abbrev
      if (/^\d{5}(-\d{4})?$/.test(secondLastWord)) {
        const lastWordUpper = lastWord.toUpperCase().replace(/[^A-Z]/g, '');
        if (US_STATE_ABBREVIATIONS.includes(lastWordUpper)) {
          return { 
            isValid: false, 
            error: 'Street address should not include ZIP code. Use the ZIP Code field instead.' 
          };
        }
      }
    }
  }
  
  // Common street suffixes that should NOT trigger state abbreviation errors
  const STREET_SUFFIXES = new Set([
    'CT', 'ST', 'DR', 'RD', 'LN', 'AVE', 'PL', 'CIR', 'WAY', 'BLVD',
    'TER', 'HWY', 'PKY', 'SQ', 'LOOP', 'PT', 'CV', 'BND', 'PASS'
  ]);
  
  // Directional tokens commonly used in USPS street addresses (cardinal + ordinal).
  // These must NOT be flagged as state abbreviations — notably "NE" collides with
  // Nebraska's abbreviation but is a legitimate directional suffix in addresses
  // like "704 TOMASITA ST NE". Likewise single letters N/S/E/W commonly lead
  // street names ("100 N 3RD ST"). Whitelist both forms.
  const STREET_DIRECTIONALS = new Set([
    'N', 'S', 'E', 'W',
    'NE', 'NW', 'SE', 'SW'
  ]);
  
  // Check for state abbreviations (as standalone words, case-insensitive)
  // Must be at the end and NOT be a common street suffix or directional
  const wordsUpper = trimmedAddress.toUpperCase().split(/\s+/);
  if (wordsUpper.length >= 2) {
    const lastWord = wordsUpper[wordsUpper.length - 1].replace(/[^A-Z]/g, '');
    
    // Only flag if it's a state abbreviation AND not a street suffix/directional
    if (US_STATE_ABBREVIATIONS.includes(lastWord) && 
        !STREET_SUFFIXES.has(lastWord) && 
        !STREET_DIRECTIONALS.has(lastWord) &&
        lastWord.length === 2) {
      // Additional check: ensure the previous word is not a number (street address)
      const prevWord = wordsUpper[wordsUpper.length - 2];
      if (!/^\d+$/.test(prevWord)) {
        return { 
          isValid: false, 
          error: `Street address should not include state abbreviation "${lastWord}". Use the State field instead.` 
        };
      }
    }
  }
  
  // Check if the city name appears in the address followed by state/ZIP markers
  // — i.e., the user pasted a full address into the street field. We
  // intentionally do NOT block when the city name appears as part of a
  // legitimate street name (e.g., "9053 N Clio Rd" with City=Clio is valid
  // because the only "Clio" token is the street's name, not a duplicated
  // city marker). May 5, 2026 — false-positive fix per ops report.
  //
  // Rule: only flag if the city token is followed (within the next ~3
  // tokens, allowing optional comma) by:
  //   - a US state abbreviation, OR
  //   - a 5-digit ZIP, OR
  //   - "<state-name> <zip>" sequence
  // The earlier comma / ZIP / state-suffix checks above already catch
  // "Clio Rd, Clio, MI 48420" style inputs via their respective rules,
  // so this check is now strictly an additional safety net for cases
  // like "9053 N Clio Rd Clio MI 48420" (no commas).
  if (city && city.trim().length > 2) {
    const cityEsc = city.trim().toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // City word followed by a state abbreviation or ZIP within the next
    // 1-3 whitespace-separated tokens (commas already stripped earlier
    // because comma input is rejected above, but allow optional comma
    // here for forward-compat).
    //
    // BUGFIX Jul 23, 2026 — state abbreviations that collide with street
    // suffixes/directionals (CT = Court/Connecticut, NE = Northeast/Nebraska)
    // must NOT trigger on their own: "6340 Westland Ct" with City=Westland is
    // a valid street line. Ambiguous tokens only count when followed by a ZIP
    // ("... Westland CT 48185"), which is a genuine pasted full address.
    const AMBIGUOUS_STATE_TOKENS = US_STATE_ABBREVIATIONS.filter(
      s => STREET_SUFFIXES.has(s) || STREET_DIRECTIONALS.has(s));
    const safeStateAlt = US_STATE_ABBREVIATIONS
      .filter(s => !AMBIGUOUS_STATE_TOKENS.includes(s)).join('|');
    const zipPat = '\\d{5}(?:-\\d{4})?\\b';
    const fullAddrPattern = new RegExp(
      `\\b${cityEsc}\\b\\s*,?\\s+(?:(?:${safeStateAlt})\\b|${zipPat})`,
      'i'
    );
    const ambiguousStatePattern = AMBIGUOUS_STATE_TOKENS.length
      ? new RegExp(
          `\\b${cityEsc}\\b\\s*,?\\s+(?:${AMBIGUOUS_STATE_TOKENS.join('|')})\\b\\s*,?\\s+${zipPat}`,
          'i')
      : null;
    if (fullAddrPattern.test(trimmedAddress) ||
        (ambiguousStatePattern && ambiguousStatePattern.test(trimmedAddress))) {
      return {
        isValid: false,
        error: `Street address looks like a full address. Enter only the street (e.g., "9053 N Clio Rd"). Use the City/State/ZIP fields for the rest.`
      };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * ZIP Code API utilities
 */
const zipCodeAPI = {
  lookupZipCode: async (zipCode) => {
    try {
      const response = await axios.post(`${API}/zip/lookup`, {
        zip_code: zipCode.trim()
      });
      return response.data;
    } catch (error) {
      console.error('ZIP code lookup error:', error);
      throw error;
    }
  }
};

/**
 * Create Order Form Component
 * 
 * @param {Function} onSubmit - Form submission handler
 * @param {Object} initialData - Initial form data (for edit mode)
 * @param {boolean} isEditMode - Whether form is in edit mode
 */
export const CreateOrderForm = ({ onSubmit, onDirtyChange, onCancel, initialData = null, isEditMode = false, productWorkflowType = null }) => {
  // Utility function: Convert text to Running Caps (Title Case)
  const toRunningCaps = (str) => {
    if (!str) return str;
    return str
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const [formData, setFormData] = useState(initialData || {
    product_id: '',
    client_code: '',
    client_product: '',
    contract_code: '',
    borrower_name: '',
    seller_name: '',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip_code: '',
    property_county: '',
    priority: 'normal',
    priority_notes: '',
    notes: '',
    parcel: ''
  });
  
  const [clientCodes, setClientCodes] = useState([]);
  // ORDER-NOTES-3000: catalog workflow_type of the selected product (drives the notes limit)
  const [selectedProductWorkflow, setSelectedProductWorkflow] = useState(productWorkflowType || initialData?.workflow_type || null);
  // ORDER-NOTES-3000: Title Search products get 3,000 chars, Tax products keep 150
  const notesMaxLength = getNotesMaxLength(formData.client_product, selectedProductWorkflow);
  const isTitleProduct = notesMaxLength > 150;
  const notesLength = (formData.notes || '').length;
  const notesWarningThreshold = Math.floor(notesMaxLength * 0.95);
  const notesAtLimit = notesLength >= notesMaxLength;
  const notesOverLimitLegacy = isEditMode && notesLength > notesMaxLength && notesLength <= (initialData?.notes || '').length;
  const [countyOptions, setCountyOptions] = useState([]);
  const [isMultiCounty, setIsMultiCounty] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priorSearchFiles, setPriorSearchFiles] = useState([]); // TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001 (create mode only)
  const [productCount, setProductCount] = useState(null); // UI-002: "View All Products (N)" pill
  const formRef = useRef(null);

  // Track dirty state — notify parent when form has user-entered data
  useEffect(() => {
    if (onDirtyChange) {
      const isDirty = !!(
        formData.borrower_name || formData.property_address ||
        formData.property_city || formData.property_state ||
        formData.property_zip_code || formData.notes || formData.parcel ||
        formData.priority_notes
      );
      onDirtyChange(isDirty);
    }
  }, [formData, onDirtyChange]);

  useEffect(() => {
    fetchClientCodes();
  }, []);

  // Update form data when initialData changes (for edit mode) — with
  // pristine-snapshot guard to prevent background refresh / parent re-renders
  // from overwriting user edits.
  //
  // Why this exists:
  //   OrderDeskEditModal (App.js) passes `initialData` as a fresh object
  //   literal on every parent render. Any parent re-render (fetchFullOrderDetails
  //   completing, notifications poll, list refresh, etc.) used to fire this
  //   effect and call setFormData(initialData), wiping in-progress user edits.
  //
  // How the guard works:
  //   - initialSnapshotRef stores the JSON string of the most recent initialData
  //     the form accepted as its source of truth.
  //   - When a new initialData arrives, we skip entirely if the content matches
  //     the last snapshot (new reference, same data — common case).
  //   - If content really did change (e.g., the async /details fetch returned
  //     richer data), we only rehydrate if formData still equals the previous
  //     snapshot — i.e. the user hasn't touched anything yet. Once dirty,
  //     local edits win until Save / Cancel / close.
  const initialSnapshotRef = useRef(JSON.stringify(initialData));
  useEffect(() => {
    if (!initialData || !isEditMode) return;
    const newSnap = JSON.stringify(initialData);
    if (newSnap === initialSnapshotRef.current) return;          // same content, no-op
    const wasPristine = JSON.stringify(formData) === initialSnapshotRef.current;
    initialSnapshotRef.current = newSnap;
    if (wasPristine) setFormData(initialData);                   // safe: user hasn't edited
  }, [initialData, isEditMode, formData]);

  const fetchClientCodes = async () => {
    try {
      // CLIENT-CODE-INACTIVE-001: only active codes are offered for new usage.
      const response = await axios.get(`${API}/client-codes`, { params: { active: true } });
      let codes = response.data || [];
      // Edit mode: keep the order's existing (possibly inactive) client code visible.
      const current = initialData?.client_code;
      if (isEditMode && current && !codes.some((c) => c.code === current)) {
        codes = [...codes, { id: `inactive-${current}`, code: current, client_name: '', inactive: true }];
      }
      setClientCodes(codes);
    } catch (error) {
      console.error('Error fetching client codes:', error);
    }
  };

  const handleZipCodeChange = async (zip) => {
    setFormData({...formData, property_zip_code: zip});
    
    if (zip.length === 5 && /^\d{5}$/.test(zip)) {
      try {
        const result = await zipCodeAPI.lookupZipCode(zip);
        
        if (result.is_valid && result.results && result.results.length > 0) {
          const primaryCity = result.primary_city || result.results[0];
          
          // Auto-populate fields with primary city
          setFormData(prev => ({
            ...prev,
            property_city: primaryCity.city,
            property_state: primaryCity.state,
            property_county: primaryCity.county
          }));
          
          setCountyOptions([]);
          setIsMultiCounty(false);
          
        } else {
          // Clear auto-populated fields on invalid ZIP
          setFormData(prev => ({
            ...prev,
            property_city: '',
            property_state: '',
            property_county: ''
          }));
          setCountyOptions([]);
          setIsMultiCounty(false);
          toast.error('Unable to auto-fill from ZIP code. Please enter city, state, and county manually.');
        }
      } catch (error) {
        console.error('ZIP lookup error:', error);
        toast.error('Unable to auto-fill from ZIP code. Please enter city, state, and county manually.');
        
        setFormData(prev => ({
          ...prev,
          property_city: '',
          property_state: '',
          property_county: ''
        }));
        setCountyOptions([]);
        setIsMultiCounty(false);
      }
    } else {
      if (zip.length === 0 || zip.length > 5) {
        setFormData(prev => ({
          ...prev,
          property_city: '',
          property_state: '',
          property_county: ''
        }));
      }
      setCountyOptions([]);
      setIsMultiCounty(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Mandatory fields validation
    if (!formData.product_id || !String(formData.product_id).trim()) newErrors.product_id = 'Product ID is required';
    if (!formData.client_code) newErrors.client_code = 'Client Code is required';
    if (!formData.client_product) newErrors.client_product = 'Client Product is required';
    if (!formData.borrower_name) newErrors.borrower_name = 'Borrower name is required';
    
    // Property mandatory fields
    if (!formData.property_address) {
      newErrors.property_address = 'Property address is required';
    } else {
      // Validate street address doesn't contain city/state/zip
      const addressValidation = validateStreetAddressOnly(formData.property_address, formData.property_city);
      if (!addressValidation.isValid) {
        newErrors.property_address = addressValidation.error;
      }
    }
    if (!formData.property_city) newErrors.property_city = 'City is required';
    if (!formData.property_state) newErrors.property_state = 'State is required';
    if (!formData.property_zip_code) newErrors.property_zip_code = 'ZIP code is required';
    if (!formData.property_county) newErrors.property_county = 'County is required';
    
    // Priority notes validation
    if ((formData.priority === 'rush' || formData.priority === 'urgent') && !formData.priority_notes) {
      newErrors.priority_notes = 'Notes are required for Rush and Urgent orders';
    }
    
    // Parcel validation
    if (formData.parcel && formData.parcel.length > 90) {
      newErrors.parcel = 'Parcel cannot exceed 90 characters';
    }

    // ORDER-NOTES-3000: product-specific Order Notes limit. In edit mode, historical
    // notes already over the limit stay saveable as long as they are not grown.
    if (formData.notes && formData.notes.length > notesMaxLength) {
      const baselineLength = isEditMode ? (initialData?.notes || '').length : 0;
      if (formData.notes.length > baselineLength) {
        newErrors.notes = `Order Notes cannot exceed ${formatCount(notesMaxLength)} characters`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) {
      toast.error('Order not submitted', {
        description: 'Please fix the highlighted required fields and try again.',
      });
      return;
    }
    {
      setIsSubmitting(true);
      try {
        // PRODUCT-ID-NORMALIZE-001: trim + strip spaces around separators ("409439 - 3" → "409439-3")
        const trimmedProductId = normalizeProductId(formData.product_id);
        const payload = trimmedProductId === formData.product_id ? formData : { ...formData, product_id: trimmedProductId };
        // Prior Search Package files ride along as a non-JSON field; the parent strips them and uploads AFTER the order exists.
        await onSubmit(!isEditMode && priorSearchFiles.length ? { ...payload, prior_search_files: priorSearchFiles } : payload);
        resetForm();
        setPriorSearchFiles([]);
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      client_code: '',
      client_product: '',
      contract_code: '',
      borrower_name: '',
      seller_name: '',
      property_address: '',
      property_city: '',
      property_state: '',
      property_zip_code: '',
      property_county: '',
      priority: 'normal',
      priority_notes: '',
      notes: '',
      parcel: ''
    });
    setCountyOptions([]);
    setIsMultiCounty(false);
    setErrors({});
    setIsSubmitting(false);
  };

  const getCurrentDateTime = () => {
    try {
      // Always display in Business Time (America/New_York) regardless of user's location
      return formatInTimeZone(new Date(), ACCOUNT_TZ, 'M/d/yyyy, h:mm:ss a') + ' ET';
    } catch (error) {
      return new Date().toLocaleString() + ' (Local)';
    }
  };

  const focusProductSearch = () => {
    const el = formRef.current?.querySelector('[data-testid="product-search-input"]');
    if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.focus({ preventScroll: true }); }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="co-form w-full space-y-3" data-testid="create-order-form">
      {/* Auto-generated Order Date (Business Time — behaviour unchanged) */}
      <div className="co-section flex items-center justify-between gap-3 rounded-xl border border-indigo-100/80 bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-violet-50/80 px-3.5 py-2" style={{ '--co-i': 0 }} data-testid="order-date-bar">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-indigo-500" />
          <Label className="text-xs font-medium text-slate-600">Order Date (Business Time – ET)</Label>
        </div>
        <p className="text-sm font-semibold tabular-nums text-slate-800">{getCurrentDateTime()}</p>
      </div>

      {/* 1 — Required Information */}
      <SectionCard index={1} title="Required Information" icon={Hash}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <FieldLabel required>Product ID</FieldLabel>
            <Input 
              value={formData.product_id}
              onChange={(e) => setFormData({...formData, product_id: e.target.value})}
              onBlur={(e) => {
                const normalized = normalizeProductId(e.target.value);
                if (normalized !== e.target.value) setFormData((prev) => ({ ...prev, product_id: normalized }));
              }}
              placeholder="Enter product ID"
              maxLength={20}
              className={`${INPUT} ${errors.product_id ? INPUT_ERR : ''} ${formData.product_id.length === 20 ? 'border-orange-300' : ''}`}
              data-testid="input-product-id"
            />
            <FieldError msg={errors.product_id} />
          </div>

          <div className="space-y-1.5">
            <FieldLabel required>Client Code</FieldLabel>
            <Select 
              key={`client-code-${formData.client_code}`}
              value={formData.client_code} 
              onValueChange={(value) => setFormData({...formData, client_code: value})}
            >
              <SelectTrigger 
                className={`${INPUT} ${errors.client_code ? INPUT_ERR : ''}`}
                data-testid="select-client-code"
              >
                <SelectValue placeholder="Choose a client code">
                  {formData.client_code ? clientCodes.find(c => c.code === formData.client_code)?.code || formData.client_code : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {clientCodes.map((code) => (
                  <SelectItem key={code.id} value={code.code} data-testid={`client-code-option-${code.code}`}>
                    <span className={`text-sm ${code.inactive ? 'text-slate-400' : ''}`}>{code.code}{code.client_name ? ` - ${code.client_name}` : ''}{code.inactive ? ' (Inactive)' : ''}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError msg={errors.client_code} />
          </div>

          {/* Contract Code — CONTRACT-CODE-MANUAL-001 (behaviour unchanged) */}
          <ContractCodeField
            value={formData.contract_code}
            clientCode={formData.client_code}
            clientProduct={formData.client_product}
            onChange={(v) => setFormData({...formData, contract_code: v})}
          />
        </div>
      </SectionCard>

      {/* 2 — Select Product */}
      <SectionCard
        index={2}
        title="Select Product"
       
        required
        icon={LayoutGrid}
        action={productCount != null && (
          <button type="button" onClick={focusProductSearch} data-testid="view-all-products-pill"
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/70 px-2.5 text-xs font-semibold text-indigo-700 transition-colors hover:border-indigo-200 hover:bg-indigo-50">
            <LayoutGrid className="h-3.5 w-3.5" /> View All Products ({productCount})
          </button>
        )}
      >
        <ClientProductSelector
          value={formData.client_product}
          onSelect={(value, meta) => {
            setSelectedProductWorkflow(meta?.workflow_type || null);
            setFormData({...formData, client_product: value});
          }}
          onCountChange={setProductCount}
        />
        <FieldError msg={errors.client_product} className="mt-2" />
      </SectionCard>

      {/* 3 — Borrower & Seller */}
      <SectionCard index={3} title="Borrower & Seller Information" icon={Users}>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <FieldLabel required>Borrower Name</FieldLabel>
            <Input 
              value={formData.borrower_name}
              onChange={(e) => setFormData({...formData, borrower_name: e.target.value})}
              placeholder="Enter borrower full name"
              maxLength={90}
              className={`${INPUT} ${errors.borrower_name ? INPUT_ERR : ''} ${formData.borrower_name.length === 90 ? 'border-orange-300' : ''}`}
              data-testid="input-borrower-name"
            />
            {/* Live preview of how the name will be saved (mirrors backend normalization). */}
            {formData.borrower_name && formData.borrower_name.trim() &&
              normalizeBorrowerName(formData.borrower_name) !== formData.borrower_name && (
                <SavePreview value={normalizeBorrowerName(formData.borrower_name)} testId="borrower-name-preview" />
              )}
            <FieldError msg={errors.borrower_name} />
          </div>

          {/* SELLER-NAME-001 — optional seller(s) / current owner; same "A and B" convention as Borrower */}
          <div className="space-y-1.5">
            <FieldLabel optional>Seller Name(s)</FieldLabel>
            <Input
              value={formData.seller_name || ''}
              onChange={(e) => setFormData({...formData, seller_name: e.target.value})}
              placeholder="Enter seller(s) — e.g. John Smith and Jane Smith"
              maxLength={200}
              className={INPUT}
              data-testid="input-seller-name"
            />
            {formData.seller_name && formData.seller_name.trim() &&
              normalizeBorrowerName(formData.seller_name) !== formData.seller_name && (
                <SavePreview value={normalizeBorrowerName(formData.seller_name)} testId="seller-name-preview" />
              )}
          </div>
        </div>
      </SectionCard>

      {/* 4 — Property Details */}
      <SectionCard index={4} title="Property Details" icon={MapPin}>
        <div className="mb-3 space-y-1.5">
          <FieldLabel required>Street Address</FieldLabel>
          <Input 
            value={formData.property_address}
            onChange={(e) => {
              const newAddress = e.target.value;
              setFormData({...formData, property_address: newAddress});
              // Real-time validation for address format
              const validation = validateStreetAddressOnly(newAddress, formData.property_city);
              if (!validation.isValid) {
                setErrors(prev => ({...prev, property_address: validation.error}));
              } else if (errors.property_address && !errors.property_address.includes('required')) {
                setErrors(prev => {
                  const newErrors = {...prev};
                  delete newErrors.property_address;
                  return newErrors;
                });
              }
            }}
            placeholder="Enter street address only (e.g., 123 Main St)"
            maxLength={100}
            className={`${INPUT} ${errors.property_address ? INPUT_ERR : ''} ${formData.property_address.length === 100 ? 'border-orange-300' : ''}`}
            data-testid="input-property-address"
          />
          {formData.property_address && formData.property_address.trim() &&
            normalizeStreetAddress(formData.property_address) !== formData.property_address && (
              <SavePreview value={normalizeStreetAddress(formData.property_address)} testId="property-address-preview" />
            )}
          <FieldError msg={errors.property_address} />
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="space-y-1.5">
            <FieldLabel required>ZIP Code</FieldLabel>
            <Input 
              value={formData.property_zip_code}
              onChange={(e) => handleZipCodeChange(e.target.value)}
              placeholder="12345"
              maxLength={5}
              className={`${INPUT} ${errors.property_zip_code ? INPUT_ERR : ''}`}
              data-testid="input-zip-code"
            />
            <FieldError msg={errors.property_zip_code} />
          </div>

          <div className="space-y-1.5">
            <FieldLabel required>City</FieldLabel>
            <Input 
              value={formData.property_city}
              onChange={(e) => setFormData({...formData, property_city: e.target.value})}
              placeholder="City name"
              className={`${INPUT} ${errors.property_city ? INPUT_ERR : ''}`}
              data-testid="input-city"
            />
            <FieldError msg={errors.property_city} />
          </div>

          <div className="space-y-1.5">
            <FieldLabel required>State</FieldLabel>
            <Input 
              value={formData.property_state}
              onChange={(e) => setFormData({...formData, property_state: e.target.value})}
              placeholder="ST"
              maxLength={2}
              className={`${INPUT} ${errors.property_state ? INPUT_ERR : ''}`}
              data-testid="input-state"
            />
            <FieldError msg={errors.property_state} />
          </div>

          <div className="space-y-1.5">
            <FieldLabel required>County</FieldLabel>
            {isMultiCounty ? (
              <Select onValueChange={(value) => setFormData({...formData, property_county: value})}>
                <SelectTrigger className={`${INPUT} ${errors.property_county ? INPUT_ERR : ''}`}>
                  <SelectValue placeholder="Select county" />
                </SelectTrigger>
                <SelectContent>
                  {countyOptions.map((county) => (
                    <SelectItem key={county} value={county}>
                      <span className="text-sm">{county}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input 
                value={formData.property_county}
                onChange={(e) => setFormData({...formData, property_county: e.target.value})}
                placeholder="County name"
                className={`${INPUT} ${errors.property_county ? INPUT_ERR : ''}`}
                data-testid="input-county"
              />
            )}
            <FieldError msg={errors.property_county} />
          </div>
        </div>
      </SectionCard>

      {/* 5 — Additional Information */}
      <SectionCard index={5} title="Additional Information" icon={SlidersHorizontal}>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="space-y-1.5">
              <FieldLabel>Priority Level</FieldLabel>
              <Select 
                value={formData.priority} 
                onValueChange={(value) => setFormData({...formData, priority: value})}
              >
                <SelectTrigger className={INPUT} data-testid="select-priority">
                  <SelectValue placeholder="Select priority level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-green-500"></span>Normal</div>
                  </SelectItem>
                  <SelectItem value="rush">
                    <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-yellow-500"></span>Rush</div>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-orange-500"></span>Urgent</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Parcel</FieldLabel>
              <Input 
                value={formData.parcel}
                onChange={(e) => setFormData({...formData, parcel: e.target.value})}
                placeholder="Enter parcel info"
                maxLength={90}
                className={`${INPUT} ${errors.parcel ? 'border-red-400' : ''} ${formData.parcel.length === 90 ? 'border-red-300' : ''}`}
                data-testid="input-parcel"
              />
              {formData.parcel && (
                <p className={`text-xs ${formData.parcel.length >= 85 ? 'text-orange-600' : 'text-slate-500'}`}>
                  {formData.parcel.length}/90 characters
                </p>
              )}
              {errors.parcel && <p className="text-xs text-red-500">{errors.parcel}</p>}
            </div>
          </div>

          {/* Priority Justification (conditional) */}
          {(formData.priority === 'rush' || formData.priority === 'urgent') && (
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-red-700">Priority Justification *</Label>
              <Textarea 
                value={formData.priority_notes}
                onChange={(e) => setFormData({...formData, priority_notes: e.target.value})}
                placeholder="Explain why this order requires rush/urgent priority..."
                className={`${INPUT} ${errors.priority_notes ? 'border-red-500' : ''}`}
                rows={2}
                data-testid="input-priority-notes"
              />
              {errors.priority_notes && <p className="text-xs text-red-500">{errors.priority_notes}</p>}
            </div>
          )}

          {/* Order Notes — ORDER-NOTES-3000 */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <FieldLabel data-testid="order-notes-label">Order Notes</FieldLabel>
              <span
                className={`text-xs tabular-nums ${notesAtLimit ? 'font-semibold text-red-600' : notesLength >= notesWarningThreshold ? 'text-orange-600' : 'text-slate-500'}`}
                data-testid="order-notes-counter"
                aria-live="polite"
              >
                {formatCount(notesLength)} / {formatCount(notesMaxLength)}
              </span>
            </div>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              maxLength={notesMaxLength}
              className={`${INPUT} ${notesAtLimit || errors.notes ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
              rows={isTitleProduct ? 4 : 2}
              data-testid="input-notes"
            />
            {(notesAtLimit || errors.notes) && (
              <p className={`flex items-center gap-1 text-xs ${notesOverLimitLegacy && !errors.notes ? 'text-amber-700' : 'text-red-600'}`} data-testid="order-notes-limit-message">
                <span className={`h-1 w-1 rounded-full ${notesOverLimitLegacy && !errors.notes ? 'bg-amber-500' : 'bg-red-500'}`}></span>
                {errors.notes || (notesOverLimitLegacy
                  ? `Existing notes exceed the ${formatCount(notesMaxLength)}-character limit — they can be kept or shortened, but not extended`
                  : notesLength > notesMaxLength
                    ? `Order Notes cannot exceed ${formatCount(notesMaxLength)} characters`
                    : `Maximum of ${formatCount(notesMaxLength)} characters reached — further input is blocked`)}
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      {/* TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001 — optional Prior Search Package (create mode only) */}
      {!isEditMode && (
        <div className="co-section" style={{ '--co-i': 6 }}>
          <PriorSearchPackagePicker files={priorSearchFiles} onChange={setPriorSearchFiles} disabled={isSubmitting} />
        </div>
      )}

      {/* Sticky footer — Cancel | Create Order (gutter follows the host scroll container via --co-gutter) */}
      <div
        className="sticky bottom-0 z-10 mx-[calc(var(--co-gutter,0px)*-1)] mt-2 flex items-center justify-end gap-2.5 border-t border-slate-200/80 bg-white/95 px-[var(--co-gutter,0px)] py-3 backdrop-blur-sm"
        data-testid="order-form-footer"
      >
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => (onCancel ? onCancel() : resetForm())} 
          className="h-10 rounded-xl border-slate-300 px-5 font-medium text-slate-700 hover:bg-slate-50"
          data-testid="cancel-order-form"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="co-cta h-10 rounded-xl border-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-5 font-semibold text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,.6)] hover:bg-gradient-to-r disabled:opacity-70"
          disabled={isSubmitting}
          data-testid="submit-order"
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              {isEditMode ? 'Saving Changes...' : 'Creating Order...'}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {isEditMode ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {isEditMode ? 'Save Changes' : 'Create Order'}
              {!isEditMode && <Sparkles className="h-3.5 w-3.5 text-violet-200" aria-hidden />}
            </div>
          )}
        </Button>
      </div>
    </form>
  );
};

export default CreateOrderForm;
